import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Slider,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { keyframes } from '@mui/system';
import {
  AutoAwesome as AutoAwesomeIcon,
  PlayArrow as PlayArrowIcon,
  Save as SaveIcon,
  Schedule as ScheduleIcon,
  Bolt as BoltIcon,
  AccessTime as AccessTimeIcon,
  SmartToy as SmartToyIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Stop as StopIcon,
  Savings as SavingsIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Email as EmailIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  getVisionReviewSettings,
  updateVisionReviewSettings,
  listVisionReviewBatchJobs,
  cancelVisionReviewBatchJob,
  runVisionReviewScheduledNow,
} from '../utils/api';
import { parseMailRecipients } from './alertRules/constants';
import { useAuth } from '../contexts/AuthContext';
import PageHeader from './PageHeader';

const PRIMARY = '#40518A';
const PAGE_SUBTITLE = '#6b7280';
const CARD_SHADOW = '0 8px 24px rgba(15, 23, 42, 0.08)';
const BATCH_JOBS_POLL_MS = 5000;
const ACTIVE_BATCH_JOB_STATUSES = new Set(['queued', 'running']);

const DEFAULT_BATCH_ALERT_SUBJECT = '[AI Matching] Batch job failure — {failure_type}';

const DEFAULT_BATCH_ALERT_BODY = `An AI Matching batch job reported a failure from the batches API.

Failure type: {failure_type}
Failure reason: {failure_reason}

Batch job ID: {batch_job_id}
Provider batch ID: {provider_batch_id}
Status: {status}
Processing status: {processing_status}
Started at: {started_at}
Completed at: {completed_at}
Requested by: {requester}

Job totals: {total} total · {submitted} submitted · {processed} processed
Batch API counts: {errored} errored · {expired} expired · {canceled} canceled
Failed audit results: {failed_audits}`;

const BATCH_ALERT_PLACEHOLDERS = [
  '{failure_type}',
  '{failure_reason}',
  '{batch_job_id}',
  '{provider_batch_id}',
  '{status}',
  '{processing_status}',
  '{started_at}',
  '{completed_at}',
  '{requester}',
  '{total}',
  '{submitted}',
  '{processed}',
  '{errored}',
  '{expired}',
  '{canceled}',
  '{failed_audits}',
];

const SAMPLE_BATCH_ALERT_CONTEXT = {
  failure_type: 'batch_api_request_error',
  failure_reason: '3 batch API request(s) errored',
  batch_job_id: 'sample-batch-job-id',
  provider_batch_id: 'msgbatch_sample123',
  status: 'completed',
  processing_status: 'ended',
  started_at: '2026-07-07T10:15:00Z',
  completed_at: '2026-07-07T10:45:00Z',
  requester: 'admin',
  total: '120',
  submitted: '118',
  processed: '118',
  errored: '3',
  expired: '0',
  canceled: '0',
  failed_audits: '3',
};

const applyBatchAlertTemplate = (template, context) => {
  let text = String(template || '');
  Object.entries(context).forEach(([key, value]) => {
    text = text.split(`{${key}}`).join(String(value ?? ''));
  });
  return text;
};

const mapBatchAlertFromSettings = (data = {}) => ({
  enabled: Boolean(data.batch_alert_enabled),
  recipients: parseMailRecipients(data.batch_alert_recipients),
  mail_subject: String(data.batch_alert_mail_subject || DEFAULT_BATCH_ALERT_SUBJECT),
  mail_body: String(data.batch_alert_mail_body || DEFAULT_BATCH_ALERT_BODY),
  email_delivery_ready: Boolean(data.batch_alert_email_delivery_ready),
});

const BatchAlertRecipientsInput = ({ recipients = [], onChange, disabled = false }) => {
  const [input, setInput] = useState('');

  const addEmail = () => {
    const email = String(input || '').trim().toLowerCase();
    if (!email) return;
    if (recipients.includes(email)) {
      setInput('');
      return;
    }
    onChange([...recipients, email]);
    setInput('');
  };

  const removeAt = (index) => {
    onChange(recipients.filter((_, i) => i !== index));
  };

  return (
    <Box>
      <TextField
        fullWidth
        size="small"
        label="Add recipient email"
        placeholder="ops@company.com"
        value={input}
        disabled={disabled}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            addEmail();
          } else if (e.key === 'Backspace' && !input && recipients.length) {
            removeAt(recipients.length - 1);
          }
        }}
        onBlur={addEmail}
      />
      {recipients.length > 0 && (
        <Stack direction="row" flexWrap="wrap" useFlexGap sx={{ gap: 0.75, mt: 1 }}>
          {recipients.map((email, index) => (
            <Chip
              key={`${email}-${index}`}
              label={email}
              size="small"
              onDelete={disabled ? undefined : () => removeAt(index)}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};

const isActiveBatchJob = (job) => ACTIVE_BATCH_JOB_STATUSES.has(job?.status);

const hasActiveBatchJobs = (jobs) =>
  Array.isArray(jobs) && jobs.some(isActiveBatchJob);

const PRICING_TIERS = {
  premium: {
    label: 'Standard rate',
    color: '#c2410c',
    colorMuted: '#ea580c',
    bg: '#fff7ed',
    bgSelected: '#ffedd5',
    border: 'rgba(234, 88, 12, 0.45)',
    borderSoft: 'rgba(251, 146, 60, 0.35)',
    insightTitle: 'Higher API cost',
    insightBody: 'Matches are processed one at a time, so this mode is the priciest. Choose it only when you need instant or on-demand results.',
  },
  economy: {
    label: 'Save ~50%',
    color: '#047857',
    colorMuted: '#059669',
    bg: '#ecfdf5',
    bgSelected: '#d1fae5',
    border: 'rgba(5, 150, 105, 0.45)',
    borderSoft: 'rgba(52, 211, 153, 0.35)',
    insightTitle: 'Best value for volume',
    insightBody: 'Matches are grouped and processed together once a day, which costs about half as much as Manual or Real-time. Best pick for most teams.',
  },
};

const DEFAULT_PROCESSING_MODE = 'scheduled';

const MODE_TOOLTIPS = {
  manual: 'Uses Messages API — higher per-request cost. Best for on-demand matching from Logs.',
  realtime: 'Uses Messages API — higher per-request cost. Best when matches must run immediately.',
  scheduled: 'Uses Batches API — about 50% cheaper for bulk daily runs.',
};

const insightSlideIn = keyframes`
  from { transform: translateY(-10px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const MODES = [
  {
    value: 'manual',
    label: 'Manual',
    description: 'Run AI matching on possible-match audits yourself from the Logs page.',
    icon: AutoAwesomeIcon,
    color: '#64748b',
    bg: '#f8fafc',
    pricingTier: 'premium',
  },
  {
    value: 'realtime',
    label: 'Real-time',
    description: 'Automatically match each possible-match as soon as it is captured.',
    icon: BoltIcon,
    color: '#2563eb',
    bg: '#eff6ff',
    pricingTier: 'premium',
  },
  {
    value: 'scheduled',
    label: 'Scheduled batch',
    description: 'Collect possible-matches and match them once per day at a set time.',
    icon: ScheduleIcon,
    color: PRIMARY,
    bg: '#eef2ff',
    pricingTier: 'economy',
    recommended: true,
  },
];

const STATUS_COLORS = {
  queued: 'default',
  running: 'info',
  completed: 'success',
  failed: 'error',
  cancelled: 'warning',
};

function parseScheduleTime(value) {
  const raw = String(value || '02:30:00').trim();
  const [h = '02', m = '30', s = '00'] = raw.split(':');
  const hour = String(Math.min(23, Math.max(0, parseInt(h, 10) || 0))).padStart(2, '0');
  const minute = String(Math.min(59, Math.max(0, parseInt(m, 10) || 0))).padStart(2, '0');
  const second = String(Math.min(59, Math.max(0, parseInt(s, 10) || 0))).padStart(2, '0');
  return { hour, minute, second };
}

function formatScheduleTime(hour, minute, second = '00') {
  return `${hour}:${minute}:${second}`;
}

function toTimeInputValue(hour, minute, second) {
  return formatScheduleTime(hour, minute, second);
}

function formatDisplayTime(hour, minute, second = '00') {
  const h = parseInt(hour, 10);
  const suffix = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${minute}:${second} ${suffix}`;
}

const DailyRunTimePicker = ({ value, onChange, disabled = false }) => {
  const { hour, minute, second } = parseScheduleTime(value);
  const timeValue = toTimeInputValue(hour, minute, second);

  const handleChange = (e) => {
    const [h, m, s = '00'] = e.target.value.split(':');
    onChange(formatScheduleTime(h, m, s));
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        width: '100%',
        p: { xs: 1.5, sm: 2 },
        borderRadius: 2,
        borderColor: disabled ? 'divider' : 'rgba(64, 81, 138, 0.25)',
        bgcolor: disabled ? '#f1f5f9' : 'rgba(64, 81, 138, 0.04)',
        opacity: disabled ? 0.85 : 1,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1.5,
            bgcolor: disabled ? '#94a3b8' : PRIMARY,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <AccessTimeIcon fontSize="small" />
        </Box>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#111827' }}>
            Daily run time
          </Typography>
          <Typography variant="caption" sx={{ color: PAGE_SUBTITLE }}>
            Server local time — runs once per day at {formatDisplayTime(hour, minute, second)}
          </Typography>
        </Box>
      </Stack>

      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        useFlexGap
        flexWrap="wrap"
        sx={{ width: '100%', gap: 1 }}
      >
        <TextField
          label="Hour : Minute : Second"
          type="time"
          size="small"
          fullWidth
          disabled={disabled}
          value={timeValue}
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
          inputProps={{ step: 1 }}
          sx={{
            flex: 1,
            minWidth: 0,
            '& .MuiInputBase-root': {
              bgcolor: '#fff',
            },
          }}
        />
        <Chip
          label={formatDisplayTime(hour, minute, second)}
          sx={{
            bgcolor: disabled ? '#94a3b8' : PRIMARY,
            color: '#fff',
            fontWeight: 600,
            height: 32,
            flexShrink: 0,
          }}
        />
      </Stack>
    </Paper>
  );
};

const ModePricingInsight = ({ mode, transitionKey }) => {
  const tier = PRICING_TIERS[mode.pricingTier];
  const isEconomy = mode.pricingTier === 'economy';
  const InsightIcon = isEconomy ? SavingsIcon : TrendingUpIcon;

  return (
    <Paper
      key={transitionKey}
      variant="outlined"
      sx={{
        mt: { xs: 2, sm: 2.5 },
        p: { xs: 1.5, sm: 2 },
        borderRadius: 2,
        borderColor: tier.border,
        bgcolor: tier.bg,
        animation: `${insightSlideIn} 0.45s cubic-bezier(0.22, 1, 0.36, 1) both`,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1.5,
            flexShrink: 0,
            bgcolor: tier.colorMuted,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <InsightIcon fontSize="small" />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ gap: 0.75, mb: 0.35 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: tier.color }}>
              {tier.insightTitle}
            </Typography>
            <Chip
              size="small"
              label={tier.label}
              sx={{
                height: 22,
                fontWeight: 700,
                fontSize: '0.7rem',
                bgcolor: tier.bgSelected,
                color: tier.color,
                border: `1px solid ${tier.borderSoft}`,
              }}
            />
          </Stack>
          <Typography variant="caption" sx={{ color: '#4b5563', lineHeight: 1.5, display: 'block' }}>
            {tier.insightBody}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
};

const getModeByValue = (value) =>
  MODES.find((m) => m.value === value)
  || MODES.find((m) => m.value === DEFAULT_PROCESSING_MODE)
  || MODES[0];

const isPremiumMode = (value) => getModeByValue(value).pricingTier === 'premium';

const AutomationModeCard = ({
  mode,
  selected,
  disabled,
  onSelect,
}) => {
  const Icon = mode.icon;
  const tooltip = MODE_TOOLTIPS[mode.value] || '';

  const card = (
    <Paper
      onClick={() => {
        if (!disabled) onSelect(mode.value);
      }}
      variant="outlined"
      sx={{
        position: 'relative',
        p: { xs: 1.5, sm: 2 },
        height: '100%',
        cursor: disabled ? 'not-allowed' : 'pointer',
        borderRadius: 2,
        borderWidth: selected ? 2 : 1,
        borderColor: selected ? PRIMARY : 'divider',
        bgcolor: selected ? mode.bg : '#fff',
        transition: 'border-color 0.2s, box-shadow 0.2s',
        boxShadow: selected ? '0 4px 14px rgba(64, 81, 138, 0.12)' : 'none',
        ...(!disabled && {
          '&:hover': { borderColor: PRIMARY, boxShadow: '0 4px 14px rgba(64, 81, 138, 0.08)' },
        }),
      }}
    >
      {mode.recommended && (
        <Chip
          size="small"
          label="Recommended"
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            height: 22,
            fontWeight: 700,
            fontSize: '0.68rem',
            bgcolor: 'rgba(64, 81, 138, 0.08)',
            color: PRIMARY,
            border: '1px solid rgba(64, 81, 138, 0.25)',
          }}
        />
      )}
      <Stack spacing={1}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1.5,
            bgcolor: selected ? mode.color : '#e2e8f0',
            color: selected ? '#fff' : '#64748b',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon fontSize="small" />
        </Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, pr: mode.recommended ? 6 : 0 }}>
          {mode.label}
        </Typography>
        <Typography variant="caption" sx={{ color: PAGE_SUBTITLE, lineHeight: 1.45 }}>
          {mode.description}
        </Typography>
        {mode.recommended && (
          <Typography variant="caption" sx={{ color: PRIMARY, fontWeight: 600, fontSize: '0.68rem' }}>
            ~50% lower API cost
          </Typography>
        )}
      </Stack>
    </Paper>
  );

  if (!tooltip || disabled) return card;

  return (
    <Tooltip title={tooltip} placement="top" arrow enterTouchDelay={0}>
      <Box sx={{ height: '100%' }}>{card}</Box>
    </Tooltip>
  );
};

const SectionCard = ({ title, subtitle, children, action }) => (
  <Card sx={{ width: '100%', borderRadius: { xs: 2, sm: 2 }, boxShadow: CARD_SHADOW }}>
    <CardContent
      sx={{
        p: { xs: 2, sm: 2.5, md: 3 },
        '&:last-child': { pb: { xs: 2, sm: 2.5, md: 3 } },
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
        spacing={1}
        sx={{ mb: { xs: 1.5, sm: 2 } }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: '#111827',
              fontSize: { xs: '1rem', sm: '1.05rem' },
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              variant="body2"
              sx={{
                color: PAGE_SUBTITLE,
                mt: 0.25,
                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        {action}
      </Stack>
      {children}
    </CardContent>
  </Card>
);

const VisionReview = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { refreshUser } = useAuth();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const show = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [togglingEnabled, setTogglingEnabled] = useState(false);
  const [running, setRunning] = useState(false);
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState(null);
  const [batchJobs, setBatchJobs] = useState([]);
  const [confirmToggle, setConfirmToggle] = useState(null); // pending next enabled value, or null
  const [cancelConfirmJob, setCancelConfirmJob] = useState(null);
  const [cancellingJobId, setCancellingJobId] = useState(null);
  const [modeTransitionKey, setModeTransitionKey] = useState(0);
  const [confirmPremiumMode, setConfirmPremiumMode] = useState(null);
  const [batchAlertOpen, setBatchAlertOpen] = useState(false);
  const [batchAlertLoading, setBatchAlertLoading] = useState(false);
  const [batchAlertSaving, setBatchAlertSaving] = useState(false);
  const [batchAlertSummary, setBatchAlertSummary] = useState({
    enabled: false,
    recipients: [],
    email_delivery_ready: false,
  });
  const [batchAlertForm, setBatchAlertForm] = useState({
    enabled: false,
    recipients: [],
    mail_subject: DEFAULT_BATCH_ALERT_SUBJECT,
    mail_body: DEFAULT_BATCH_ALERT_BODY,
    email_delivery_ready: false,
  });
  const pollRef = useRef(null);
  const hasActiveBatchJobsPoll = useMemo(() => hasActiveBatchJobs(batchJobs), [batchJobs]);

  const loadSettings = useCallback(async () => {
    try {
      const data = await getVisionReviewSettings();
      setSettings(data);
      setForm({
        processing_mode: data.processing_mode || DEFAULT_PROCESSING_MODE,
        use_batch_api: Boolean(data.use_batch_api),
        schedule_time: data.schedule_time || '02:30:00',
        auto_mark_attendance: Boolean(data.auto_mark_attendance),
        auto_mark_min_confidence: Number(data.auto_mark_min_confidence ?? 0),
        recent_attendance_window_seconds: Number(data.recent_attendance_window_seconds ?? 60),
      });
      const alertState = mapBatchAlertFromSettings(data);
      setBatchAlertSummary({
        enabled: alertState.enabled,
        recipients: alertState.recipients,
        email_delivery_ready: alertState.email_delivery_ready,
      });
      setBatchAlertForm(alertState);
    } catch (err) {
      show('Failed to load AI Matching settings', 'error');
    } finally {
      setLoading(false);
    }
  }, [show]);

  const loadBatchJobs = useCallback(async () => {
    try {
      const data = await listVisionReviewBatchJobs();
      setBatchJobs(Array.isArray(data) ? data : []);
    } catch (_) {
      // best-effort
    }
  }, []);

  const openBatchAlertDialog = useCallback(async () => {
    setBatchAlertOpen(true);
    if (settings) {
      setBatchAlertForm(mapBatchAlertFromSettings(settings));
      return;
    }
    setBatchAlertLoading(true);
    try {
      const data = await getVisionReviewSettings();
      setSettings(data);
      setBatchAlertForm(mapBatchAlertFromSettings(data));
      setBatchAlertSummary({
        enabled: Boolean(data.batch_alert_enabled),
        recipients: parseMailRecipients(data.batch_alert_recipients),
        email_delivery_ready: Boolean(data.batch_alert_email_delivery_ready),
      });
    } catch (err) {
      show(err.message || 'Failed to load batch alert settings', 'error');
      setBatchAlertOpen(false);
    } finally {
      setBatchAlertLoading(false);
    }
  }, [settings, show]);

  const handleCloseBatchAlert = useCallback(() => {
    if (batchAlertSaving) return;
    setBatchAlertOpen(false);
  }, [batchAlertSaving]);

  const handleSaveBatchAlertSettings = useCallback(async () => {
    setBatchAlertSaving(true);
    try {
      const updated = await updateVisionReviewSettings({
        batch_alert_enabled: batchAlertForm.enabled,
        batch_alert_recipients: batchAlertForm.recipients,
        batch_alert_mail_subject: batchAlertForm.mail_subject,
        batch_alert_mail_body: batchAlertForm.mail_body,
      });
      setSettings(updated);
      const alertState = mapBatchAlertFromSettings(updated);
      setBatchAlertForm(alertState);
      setBatchAlertSummary({
        enabled: alertState.enabled,
        recipients: alertState.recipients,
        email_delivery_ready: alertState.email_delivery_ready,
      });
      show('Batch failure alert settings saved', 'success');
      setBatchAlertOpen(false);
    } catch (err) {
      show(err.message || 'Failed to save batch alert settings', 'error');
    } finally {
      setBatchAlertSaving(false);
    }
  }, [batchAlertForm, show]);

  const batchAlertPreview = useMemo(() => ({
    subject: applyBatchAlertTemplate(batchAlertForm.mail_subject, SAMPLE_BATCH_ALERT_CONTEXT),
    body: applyBatchAlertTemplate(batchAlertForm.mail_body, SAMPLE_BATCH_ALERT_CONTEXT),
  }), [batchAlertForm.mail_subject, batchAlertForm.mail_body]);

  useEffect(() => {
    loadSettings();
    loadBatchJobs();
  }, [loadSettings, loadBatchJobs]);

  useEffect(() => {
    if (!hasActiveBatchJobsPoll) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return undefined;
    }

    pollRef.current = setInterval(loadBatchJobs, BATCH_JOBS_POLL_MS);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [hasActiveBatchJobsPoll, loadBatchJobs]);

  const applyModeSelection = useCallback((nextMode) => {
    setForm((prev) => ({ ...prev, processing_mode: nextMode }));
    setModeTransitionKey((k) => k + 1);
  }, []);

  const requestModeSelection = useCallback((nextMode) => {
    if (!form || !settings?.enabled || form.processing_mode === nextMode) return;

    const leavingScheduledForPremium =
      form.processing_mode === 'scheduled' && isPremiumMode(nextMode);

    if (leavingScheduledForPremium) {
      setConfirmPremiumMode(nextMode);
      return;
    }

    applyModeSelection(nextMode);
  }, [form, settings?.enabled, applyModeSelection]);

  const cancelPremiumModeConfirm = () => {
    setConfirmPremiumMode(null);
  };

  const confirmPremiumModeSelection = () => {
    if (!confirmPremiumMode) return;
    applyModeSelection(confirmPremiumMode);
    setConfirmPremiumMode(null);
  };

  const requestMasterToggle = (nextEnabled) => {
    if (togglingEnabled) return;
    setConfirmToggle(nextEnabled);
  };

  const cancelMasterToggle = () => {
    if (togglingEnabled) return;
    setConfirmToggle(null);
  };

  const confirmMasterToggle = async () => {
    if (togglingEnabled || confirmToggle === null) return;
    const nextEnabled = confirmToggle;
    try {
      setTogglingEnabled(true);
      const updated = await updateVisionReviewSettings({ enabled: nextEnabled });
      setSettings(updated);
      if (typeof refreshUser === 'function') {
        await refreshUser();
      }
      show(nextEnabled ? 'AI Matching enabled for all modes' : 'AI Matching disabled', 'success');
      setConfirmToggle(null);
    } catch (err) {
      show('Failed to update AI Matching status', 'error');
    } finally {
      setTogglingEnabled(false);
    }
  };

  const handleSave = async () => {
    if (!form || !settings?.enabled) return;
    try {
      setSaving(true);
      const updated = await updateVisionReviewSettings(form);
      setSettings(updated);
      show('AI Matching settings saved', 'success');
    } catch (err) {
      show('Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleRunNow = async () => {
    if (!settings?.enabled) return;
    try {
      setRunning(true);
      const result = await runVisionReviewScheduledNow();
      if (result?.dispatched) {
        show(`Matching started (${result.mode}) for ${result.total} audit(s)`, 'success');
      } else {
        show(result?.reason === 'no_audits' ? 'No possible-matches to review' : 'Nothing dispatched', 'info');
      }
      loadBatchJobs();
    } catch (err) {
      show('Failed to start matching', 'error');
    } finally {
      setRunning(false);
    }
  };

  const confirmCancelBatchJob = async () => {
    if (!cancelConfirmJob?.batch_job_id || cancellingJobId) return;
    const batchJobId = cancelConfirmJob.batch_job_id;
    try {
      setCancellingJobId(batchJobId);
      await cancelVisionReviewBatchJob(batchJobId);
      show('Batch job cancelled', 'success');
      setCancelConfirmJob(null);
      loadBatchJobs();
    } catch (err) {
      show(err.message || 'Failed to cancel batch job', 'error');
    } finally {
      setCancellingJobId(null);
    }
  };

  const activeMode = useMemo(
    () => getModeByValue(form?.processing_mode),
    [form?.processing_mode],
  );

  const pendingPremiumMode = useMemo(
    () => (confirmPremiumMode ? getModeByValue(confirmPremiumMode) : null),
    [confirmPremiumMode],
  );

  if (loading || !form) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: { xs: '50vh', sm: '60vh' } }}>
        <CircularProgress sx={{ color: PRIMARY }} />
      </Box>
    );
  }

  if (!settings?.available) {
    return (
      <Box sx={{ width: '100%' }}>
        <Alert severity="warning">
          AI Vision Review is not available. Ensure <code>VISION_REVIEW_ENABLED=true</code> and the
          provider API key are configured, then restart the API.
        </Alert>
      </Box>
    );
  }

  const batchSupported = Boolean(settings?.batch_api_supported);
  const scheduledMode = form.processing_mode === 'scheduled';
  const controlsDisabled = !settings?.enabled;

  const disabledSectionSx = controlsDisabled
    ? {
        opacity: 0.48,
        pointerEvents: 'none',
        userSelect: 'none',
        filter: 'grayscale(25%)',
        transition: 'opacity 0.2s ease, filter 0.2s ease',
      }
    : undefined;

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', pb: { xs: 6, sm: 4 } }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'flex-start' },
          justifyContent: 'space-between',
          gap: 1.5,
          mb: 3,
        }}
      >
        <PageHeader
          title="AI Matching"
          subtitle={`Configure how possible-match audits are matched using ${settings?.model || 'the vision model'}.`}
        />
        <Button
          component={RouterLink}
          to="/ai-review/statistics"
          variant="outlined"
          size="small"
          startIcon={<AssessmentIcon />}
          sx={{
            alignSelf: { xs: 'flex-start', sm: 'flex-start' },
            borderColor: 'rgba(64, 81, 138, 0.35)',
            color: PRIMARY,
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}
        >
          View Statistics
        </Button>
      </Box>

      <Stack
        direction="row"
        flexWrap="wrap"
        useFlexGap
        sx={{ gap: 1, mb: { xs: 2, sm: 2.5, md: 3 } }}
      >
        <Chip
          size="small"
          icon={<SmartToyIcon />}
          label={settings?.enabled ? 'Runtime ON' : 'Runtime OFF'}
          color={settings?.enabled ? 'success' : 'default'}
          variant={settings?.enabled ? 'filled' : 'outlined'}
        />
        <Chip size="small" label={`Mode: ${activeMode.label}`} sx={{ bgcolor: activeMode.bg, fontWeight: 600 }} />
        <Chip size="small" label={`Provider: ${settings?.provider || '—'}`} variant="outlined" />
        {scheduledMode && (() => {
          const t = parseScheduleTime(form.schedule_time);
          return (
            <Chip
              size="small"
              icon={<ScheduleIcon />}
              label={`Daily at ${formatDisplayTime(t.hour, t.minute, t.second)}`}
              variant="outlined"
            />
          );
        })()}
      </Stack>

      {!settings?.enabled && (
        <Alert severity="warning" sx={{ mb: { xs: 2, sm: 2.5 } }}>
          AI Matching is turned off. Enable it below to allow manual matching from Logs, real-time auto-matching, and scheduled batches.
        </Alert>
      )}

      <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ width: '100%' }}>
        <Grid size={12}>
          <SectionCard
            title="AI Matching master switch"
            subtitle="Controls all AI matching: Logs page actions, real-time auto-matching, and scheduled batch runs."
          >
            <Paper
              variant="outlined"
              sx={{
                p: { xs: 1.5, sm: 2 },
                borderRadius: 2,
                borderColor: settings?.enabled ? 'rgba(64, 81, 138, 0.35)' : 'divider',
                bgcolor: settings?.enabled ? 'rgba(64, 81, 138, 0.06)' : '#f8fafc',
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between"
                spacing={{ xs: 1.5, sm: 2 }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body1" sx={{ fontWeight: 700, color: '#111827', fontSize: { xs: '0.95rem', sm: '1rem' } }}>
                    {settings?.enabled ? 'AI Matching is ON' : 'AI Matching is OFF'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: PAGE_SUBTITLE, display: 'block', mt: 0.25 }}>
                    {settings?.enabled
                      ? 'Manual, real-time, and scheduled AI matching are active per your mode settings.'
                      : 'No AI matching will run until you turn this on.'}
                  </Typography>
                </Box>
                <Switch
                  checked={Boolean(settings?.enabled)}
                  disabled={togglingEnabled}
                  onChange={(e) => requestMasterToggle(e.target.checked)}
                  sx={{
                    alignSelf: { xs: 'flex-start', sm: 'center' },
                    '& .MuiSwitch-switchBase.Mui-checked': { color: PRIMARY },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: PRIMARY },
                  }}
                />
              </Stack>
            </Paper>
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, lg: 8 }} sx={disabledSectionSx} aria-disabled={controlsDisabled}>
          <Stack spacing={3}>
            <SectionCard
              title="Automation mode"
              subtitle="Choose when possible-match audits are sent to the vision model. Scheduled batch is recommended for lower API cost."
            >
              <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                {MODES.map((mode) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={mode.value}>
                    <AutomationModeCard
                      mode={mode}
                      selected={form.processing_mode === mode.value}
                      disabled={controlsDisabled}
                      onSelect={requestModeSelection}
                    />
                  </Grid>
                ))}
              </Grid>

              <ModePricingInsight mode={activeMode} transitionKey={modeTransitionKey} />

              {scheduledMode && (
                <Box sx={{ mt: { xs: 2, sm: 3 } }}>
                  <Divider sx={{ mb: { xs: 2, sm: 2.5 } }} />
                  <Stack spacing={{ xs: 2, sm: 2.5 }}>
                    <DailyRunTimePicker
                      value={form.schedule_time}
                      onChange={(next) => setForm({ ...form, schedule_time: next })}
                      disabled={controlsDisabled}
                    />
                    <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }}>
                      <Tooltip title={batchSupported ? '' : 'Batch API requires the Claude provider'}>
                        <FormControlLabel
                          sx={{
                            m: 0,
                            width: '100%',
                            alignItems: 'flex-start',
                            gap: 1,
                          }}
                          control={
                            <Switch
                              checked={form.use_batch_api && batchSupported}
                              disabled={controlsDisabled || !batchSupported}
                              onChange={(e) => setForm({ ...form, use_batch_api: e.target.checked })}
                              sx={{
                                mt: 0.25,
                                '& .MuiSwitch-switchBase.Mui-checked': { color: PRIMARY },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: PRIMARY },
                              }}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                Use Anthropic Message Batches API
                              </Typography>
                              <Typography variant="caption" sx={{ color: PAGE_SUBTITLE }}>
                                Async bulk processing — about 50% cheaper than real-time messages.
                              </Typography>
                            </Box>
                          }
                        />
                      </Tooltip>
                    </Paper>
                  </Stack>
                </Box>
              )}
            </SectionCard>

            <SectionCard
              title="Attendance"
              subtitle="Optionally mark attendance automatically when AI confirms a match."
            >
              <FormControlLabel
                sx={{ m: 0, width: '100%', alignItems: 'center', gap: 1 }}
                control={
                  <Switch
                    checked={form.auto_mark_attendance}
                    disabled={controlsDisabled}
                    onChange={(e) => setForm({ ...form, auto_mark_attendance: e.target.checked })}
                    sx={{
                      mt: 0.25,
                      '& .MuiSwitch-switchBase.Mui-checked': { color: PRIMARY },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: PRIMARY },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Auto-mark attendance on AI match
                  </Typography>
                }
              />
              {form.auto_mark_attendance && (
                <Box sx={{ mt: 2, maxWidth: { xs: '100%', sm: 420 } }}>
                  <Typography variant="body2" sx={{ color: PAGE_SUBTITLE, mb: 0.5 }}>
                    Minimum confidence: <strong>{form.auto_mark_min_confidence}%</strong>
                  </Typography>
                  <Slider
                    value={form.auto_mark_min_confidence}
                    onChange={(_, v) => setForm({ ...form, auto_mark_min_confidence: v })}
                    min={0}
                    max={100}
                    step={5}
                    disabled={controlsDisabled}
                    valueLabelDisplay="auto"
                    sx={{ color: PRIMARY }}
                  />
                  <TextField
                    label="Duplicate prevention window (seconds)"
                    type="number"
                    size="small"
                    fullWidth
                    sx={{ mt: 2 }}
                    value={form.recent_attendance_window_seconds ?? 60}
                    onChange={(e) => {
                      const raw = e.target.value;
                      const parsed = raw === '' ? 0 : Number(raw);
                      setForm({
                        ...form,
                        recent_attendance_window_seconds: Number.isFinite(parsed)
                          ? Math.max(0, Math.floor(parsed))
                          : 60,
                      });
                    }}
                    disabled={controlsDisabled}
                    helperText="Skip auto-mark if this employee already has attendance within this many seconds (any camera). 0 disables."
                    inputProps={{ min: 0, step: 1 }}
                  />
                </Box>
              )}
            </SectionCard>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ width: '100%' }}>
              <Button
                variant="contained"
                size={isMobile ? 'medium' : 'large'}
                fullWidth={isMobile}
                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving || controlsDisabled}
                sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: '#344574' } }}
              >
                Save settings
              </Button>
              <Tooltip title={controlsDisabled ? 'Turn on AI Matching to run batch matching' : 'Match all unreviewed possible-matches immediately'}>
                <span style={{ width: isMobile ? '100%' : 'auto' }}>
                  <Button
                    variant="outlined"
                    size={isMobile ? 'medium' : 'large'}
                    fullWidth={isMobile}
                    startIcon={running ? <CircularProgress size={18} /> : <PlayArrowIcon />}
                    onClick={handleRunNow}
                    disabled={running || controlsDisabled}
                    sx={{ borderColor: PRIMARY, color: PRIMARY }}
                  >
                    Run batch now
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }} sx={disabledSectionSx} aria-disabled={controlsDisabled}>
          <SectionCard title="System status">
            <Stack spacing={1.25} divider={<Divider flexItem />}>
              <StatusRow label="Provider" value={settings?.provider || '—'} />
              <StatusRow label="Model" value={settings?.model || '—'} />
              <StatusRow label="Active mode" value={settings?.processing_mode || '—'} capitalize />
              <StatusRow
                label="Batch API"
                value={batchSupported ? (settings?.use_batch_api ? 'Enabled' : 'Disabled') : 'Not supported'}
              />
              <StatusRow label="Scheduled time" value={settings?.schedule_time || '—'} />
              <StatusRow label="Last scheduled run" value={settings?.last_scheduled_run || 'Never'} />
              <StatusRow label="Auto-mark" value={settings?.auto_mark_attendance ? 'On' : 'Off'} />
              <StatusRow
                label="Duplicate window"
                value={
                  settings?.auto_mark_attendance
                    ? `${Number(settings?.recent_attendance_window_seconds ?? 60)}s`
                    : '—'
                }
              />
              <StatusRow
                label="Batch failure alerts"
                value={
                  batchAlertSummary.enabled
                    ? `${batchAlertSummary.recipients.length} recipient(s)`
                    : 'Off'
                }
              />
            </Stack>
          </SectionCard>
        </Grid>

        <Grid size={12} sx={disabledSectionSx} aria-disabled={controlsDisabled}>
          <SectionCard
            title="Batch jobs"
            subtitle={
              hasActiveBatchJobsPoll
                ? 'Recent Anthropic batch submissions — refreshing while jobs are in progress.'
                : 'Recent Anthropic batch submissions.'
            }
            action={
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<EmailIcon />}
                  onClick={openBatchAlertDialog}
                  disabled={controlsDisabled}
                  sx={{ borderColor: PRIMARY, color: PRIMARY }}
                >
                  Failure alerts
                </Button>
                <Chip size="small" label={`${batchJobs.length} job(s)`} variant="outlined" />
              </Stack>
            }
          >
            {batchJobs.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <ScheduleIcon sx={{ fontSize: 40, color: '#cbd5e1', mb: 1 }} />
                <Typography variant="body2" sx={{ color: PAGE_SUBTITLE }}>
                  No batch jobs yet. Use &quot;Run batch now&quot; or enable scheduled mode.
                </Typography>
              </Box>
            ) : (
              <Stack spacing={1.5}>
                {batchJobs.map((job) => (
                  <BatchJobRow
                    key={job.batch_job_id}
                    job={job}
                    onCancel={isActiveBatchJob(job) ? () => setCancelConfirmJob(job) : null}
                    cancelling={cancellingJobId === job.batch_job_id}
                  />
                ))}
              </Stack>
            )}
          </SectionCard>
        </Grid>
      </Grid>

      <Dialog
        open={confirmToggle !== null}
        onClose={cancelMasterToggle}
        maxWidth="sm"
        fullWidth
        aria-labelledby="ai-matching-toggle-title"
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle
          id="ai-matching-toggle-title"
          sx={{
            pb: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'grey.50',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                bgcolor: confirmToggle ? '#eef2ff' : '#fef2f2',
                color: confirmToggle ? PRIMARY : '#dc2626',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {confirmToggle ? <SmartToyIcon sx={{ fontSize: 24 }} /> : <ErrorIcon sx={{ fontSize: 24 }} />}
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {confirmToggle ? 'Enable AI Matching' : 'Disable AI Matching'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {confirmToggle ? 'Applies to all matching modes' : 'This can be turned back on anytime'}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ p: 2 }}>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Alert severity={confirmToggle ? 'info' : 'warning'} sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {confirmToggle ? 'Turn on AI Matching?' : 'Turn off AI Matching?'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {confirmToggle
                  ? 'This turns on AI Matching for all modes — Logs page actions, real-time auto-matching, and scheduled batch runs will become active per your mode settings.'
                  : 'This turns off AI Matching everywhere. No manual, real-time, or scheduled matching will run until you turn it back on.'}
              </Typography>
            </Alert>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={cancelMasterToggle} variant="outlined" disabled={togglingEnabled}>
            Cancel
          </Button>
          <Button
            onClick={confirmMasterToggle}
            variant="contained"
            color={confirmToggle ? 'primary' : 'error'}
            disabled={togglingEnabled}
            startIcon={togglingEnabled ? <CircularProgress size={16} color="inherit" /> : (confirmToggle ? <SmartToyIcon /> : <ErrorIcon />)}
            sx={confirmToggle ? { bgcolor: PRIMARY, '&:hover': { bgcolor: '#344574' } } : undefined}
          >
            {togglingEnabled
              ? (confirmToggle ? 'Enabling...' : 'Disabling...')
              : (confirmToggle ? 'Enable' : 'Disable')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(confirmPremiumMode)}
        onClose={cancelPremiumModeConfirm}
        maxWidth="sm"
        fullWidth
        aria-labelledby="premium-mode-confirm-title"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle
          id="premium-mode-confirm-title"
          sx={{
            pb: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'grey.50',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                bgcolor: '#fff7ed',
                color: '#ea580c',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <TrendingUpIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Switch to {pendingPremiumMode?.label || 'this mode'}?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Higher API cost than scheduled batch
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Alert severity="warning" sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                This mode uses the Messages API
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {pendingPremiumMode?.label} costs about twice as much per match as Scheduled batch,
                which uses the Batches API. Continue only if you need immediate or on-demand matching.
              </Typography>
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={cancelPremiumModeConfirm} variant="outlined">
            Keep scheduled batch
          </Button>
          <Button
            onClick={confirmPremiumModeSelection}
            variant="contained"
            sx={{ bgcolor: '#ea580c', '&:hover': { bgcolor: '#c2410c' } }}
          >
            Use {pendingPremiumMode?.label || 'this mode'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(cancelConfirmJob)}
        onClose={() => {
          if (!cancellingJobId) setCancelConfirmJob(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle
          sx={{
            pb: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'grey.50',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                bgcolor: '#fef3c7',
                color: '#d97706',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <StopIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Cancel batch job?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Stops polling and cancels the Anthropic batch request
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Alert severity="warning" sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Cancel this AI Matching batch job?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {cancelConfirmJob?.processed ?? 0} / {cancelConfirmJob?.total ?? 0} audits processed so far.
                Audits not yet reviewed will remain unchanged.
              </Typography>
              {cancelConfirmJob?.requester && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Started by: {cancelConfirmJob.requester}
                </Typography>
              )}
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={() => setCancelConfirmJob(null)}
            variant="outlined"
            disabled={Boolean(cancellingJobId)}
          >
            Keep running
          </Button>
          <Button
            onClick={confirmCancelBatchJob}
            variant="contained"
            color="warning"
            disabled={Boolean(cancellingJobId)}
            startIcon={cancellingJobId ? <CircularProgress size={16} color="inherit" /> : <StopIcon />}
          >
            {cancellingJobId ? 'Cancelling…' : 'Cancel job'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={batchAlertOpen}
        onClose={handleCloseBatchAlert}
        maxWidth="md"
        fullWidth
        fullScreen={false}
        aria-labelledby="batch-alert-dialog-title"
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          id="batch-alert-dialog-title"
          sx={{
            fontSize: { xs: '1.1rem', sm: '1.25rem' },
            py: { xs: 1.5, sm: 2 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Batch failure email alerts
          </Typography>
          <IconButton
            aria-label="Close"
            onClick={handleCloseBatchAlert}
            size="small"
            sx={{ bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
          {batchAlertLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={2.5} mt={1}>
              <Typography variant="body2" color="text.secondary">
                Notify recipients when Anthropic batch jobs fail or return API errors.
              </Typography>
              {!batchAlertForm.email_delivery_ready && (
                <Alert severity="warning">
                  Email alerts are not configured. Please contact your administrator to configure email alerts.
                </Alert>
              )}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <FormControlLabel
                  sx={{ m: 0, width: '100%', justifyContent: 'space-between', ml: 0 }}
                  control={
                    <Switch
                      checked={batchAlertForm.enabled}
                      onChange={(e) => setBatchAlertForm((prev) => ({ ...prev, enabled: e.target.checked }))}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: PRIMARY },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: PRIMARY },
                      }}
                    />
                  }
                  labelPlacement="start"
                  label={
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 700 }}>
                        Enable batch failure alerts
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Sends one email per failed batch job (submit, collect, or API request errors).
                      </Typography>
                    </Box>
                  }
                />
              </Paper>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Recipients
                </Typography>
                <BatchAlertRecipientsInput
                  recipients={batchAlertForm.recipients}
                  onChange={(recipients) => setBatchAlertForm((prev) => ({ ...prev, recipients }))}
                  disabled={!batchAlertForm.enabled}
                />
              </Box>
              <TextField
                fullWidth
                label="Email subject template"
                value={batchAlertForm.mail_subject}
                onChange={(e) => setBatchAlertForm((prev) => ({ ...prev, mail_subject: e.target.value }))}
                disabled={!batchAlertForm.enabled}
                helperText="Use placeholders like {failure_type} and {failure_reason}."
              />
              <TextField
                fullWidth
                multiline
                minRows={8}
                label="Email body template"
                value={batchAlertForm.mail_body}
                onChange={(e) => setBatchAlertForm((prev) => ({ ...prev, mail_body: e.target.value }))}
                disabled={!batchAlertForm.enabled}
              />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                  Available placeholders
                </Typography>
                <Stack direction="row" flexWrap="wrap" useFlexGap sx={{ gap: 0.75 }}>
                  {BATCH_ALERT_PLACEHOLDERS.map((token) => (
                    <Chip key={token} label={token} size="small" variant="outlined" />
                  ))}
                </Stack>
              </Box>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'rgba(248, 250, 252, 0.8)',
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: PRIMARY, mb: 1 }}>
                  Preview (sample failure)
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Subject: {batchAlertPreview.subject}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: 'pre-wrap', color: '#374151', fontFamily: 'inherit' }}
                >
                  {batchAlertPreview.body}
                </Typography>
              </Paper>
            </Stack>
          )}
        </DialogContent>

        <Box
          sx={{
            p: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: '#fff',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box />
          <Stack direction="row" spacing={1}>
            <Button
              onClick={handleCloseBatchAlert}
              variant="outlined"
              disabled={batchAlertSaving}
              sx={{ textTransform: 'none', px: 3 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveBatchAlertSettings}
              variant="contained"
              disabled={
                batchAlertSaving
                || batchAlertLoading
                || (batchAlertForm.enabled && batchAlertForm.recipients.length === 0)
              }
              startIcon={batchAlertSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              sx={{
                textTransform: 'none',
                px: 3,
                bgcolor: PRIMARY,
                '&:hover': { bgcolor: '#344574' },
              }}
            >
              {batchAlertSaving ? 'Saving…' : 'Save alerts'}
            </Button>
          </Stack>
        </Box>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const StatusRow = ({ label, value, capitalize = false }) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="flex-start"
    spacing={1}
    sx={{ gap: 1 }}
  >
    <Typography variant="body2" sx={{ color: PAGE_SUBTITLE, flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        fontWeight: 600,
        textTransform: capitalize ? 'capitalize' : 'none',
        color: '#111827',
        textAlign: 'right',
        wordBreak: 'break-word',
      }}
    >
      {value}
    </Typography>
  </Stack>
);

const BatchJobRow = ({ job, onCancel, cancelling = false }) => {
  const counts = job.request_counts || {};
  const total = job.total || 0;
  const submitted = job.submitted || 0;
  const processed = job.processed || 0;
  const pct = total > 0 ? Math.round((processed / total) * 100) : 0;
  const isActive = isActiveBatchJob(job);
  const waitingOnProvider = isActive && (counts.processing || 0) > 0;
  const StatusIcon =
    job.status === 'completed'
      ? CheckCircleIcon
      : job.status === 'failed'
        ? ErrorIcon
        : job.status === 'cancelled'
          ? StopIcon
          : ScheduleIcon;

  return (
    <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ gap: 0.75 }}>
          <StatusIcon
            fontSize="small"
            sx={{
              color:
                job.status === 'failed'
                  ? 'error.main'
                  : job.status === 'cancelled'
                    ? 'warning.main'
                    : PRIMARY,
            }}
          />
          <Chip size="small" label={job.status} color={STATUS_COLORS[job.status] || 'default'} />
          {job.processing_status && (
            <Chip size="small" variant="outlined" label={job.processing_status} />
          )}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" sx={{ fontWeight: 600, color: PAGE_SUBTITLE, whiteSpace: 'nowrap' }}>
            {processed} / {total} ({pct}%)
          </Typography>
          {isActive && onCancel && (
            <Button
              size="small"
              variant="outlined"
              color="warning"
              disabled={cancelling}
              startIcon={cancelling ? <CircularProgress size={14} /> : <StopIcon />}
              onClick={onCancel}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
            >
              {cancelling ? 'Cancelling…' : 'Cancel'}
            </Button>
          )}
        </Stack>
      </Stack>
      {isActive && (
        <LinearProgress
          variant={total ? 'determinate' : 'indeterminate'}
          value={pct}
          sx={{ mb: 1, borderRadius: 1, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: PRIMARY } }}
        />
      )}
      <Typography
        variant="caption"
        sx={{
          color: PAGE_SUBTITLE,
          display: 'block',
          mb: 0.75,
          wordBreak: 'break-all',
          fontSize: { xs: '0.7rem', sm: '0.75rem' },
        }}
      >
        {job.provider_batch_id || job.batch_job_id}
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap">
        {typeof counts.succeeded === 'number' && counts.succeeded > 0 && (
          <Chip size="small" variant="outlined" color="success" label={`Succeeded ${counts.succeeded}`} />
        )}
        {typeof counts.errored === 'number' && counts.errored > 0 && (
          <Chip size="small" variant="outlined" color="error" label={`Failed ${counts.errored}`} />
        )}
        {waitingOnProvider && (
          <Chip size="small" variant="outlined" label={`Claude processing ${counts.processing}`} />
        )}
        {!waitingOnProvider && typeof counts.processing === 'number' && counts.processing > 0 && (
          <Chip size="small" variant="outlined" label={`Processing ${counts.processing}`} />
        )}
        {submitted > 0 && isActive && (
          <Chip size="small" variant="outlined" label={`Submitted ${submitted}`} />
        )}
        {job.requester && <Chip size="small" variant="outlined" label={`By ${job.requester}`} />}
      </Stack>
      {job.error && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.75 }}>
          {job.error}
        </Typography>
      )}
    </Paper>
  );
};

export default VisionReview;
